import { IFtthOrchestrator } from "@nju/data-access-portal-orchestrator";
import { IInternet } from "@nju/data-access-portal-product";
import { IClientAddress } from "@nju/data-access-sales-client";
import { AddAddressModal } from "@nju/feature-sales-client";
import { Icon } from "@nju/icons";
import {
  Badge,
  BlockText,
  Button,
  Heading,
  Modal,
  Panel,
  Separator,
  Text,
  useOverlay,
} from "@nju/ui";
import { capitalizeFirstLetterOfEachWord } from "@nju/utils";
import { getStatusBadge } from "../partials/getStatusBadge";
import { StatusBadge } from "../partials/StatusBadge";
import type { IPhaseProps } from "../partials/types";

export interface ICustomerStatusFtthProductCardProps {
  ftth: IFtthOrchestrator | undefined;
  phase: IPhaseProps;
  internet: IInternet | undefined;
  handleRedirectFtth: () => void;
  handleBlockFtth: () => void;
  handleCopyToClipboard: (text: string) => Promise<void>;
  isAddressInFtthRange: boolean;
  address: IClientAddress | undefined;
  onUpdateAddress: () => void;
}

export function CustomerStatusFtthProductCard({
  ftth,
  phase,
  internet,
  handleRedirectFtth,
  handleBlockFtth,
  handleCopyToClipboard,
  isAddressInFtthRange,
  address,
  onUpdateAddress,
}: ICustomerStatusFtthProductCardProps) {
  const badge = getStatusBadge({ phase, phaseType: "ftth" });
  const serviceId = internet?.characteristic.extBusinessId;
  const addressModal = useOverlay();

  return (
    <div data-cy="ftth-status-section">
      <Panel
        badge={{
          body: badge && (
            <StatusBadge icon={badge.iconName} text={badge.text} />
          ),
          variant: "ripeLemon",
        }}
        forceShowTitleBlock={true}
        height="full"
        iconName="globe"
        invisibleSeparator={true}
        separatorSize="none"
      >
        <div className="flex flex-col justify-between">
          <div>
            <Separator size="xs" tone="invisible" />
            <Heading font="ttfirs" tone="neutral" variant="h3" weight="bold">
              nju światłowód
            </Heading>
          </div>
          {isAddressInFtthRange && (
            <>
              <Separator size="xs" tone="invisible" />
              <Badge badgeText="klient w zasięgu FTTH" name="globe" />
            </>
          )}
          {address && (
            <>
              <Separator size="xxs" tone="invisible" />
              <div className="flex gap-4 items-center">
                <div>
                  <BlockText size="m">
                    {address?.[0]?.geographicalAddress?.municipality &&
                      capitalizeFirstLetterOfEachWord(
                        address?.[0]?.geographicalAddress?.municipality,
                      )}
                    ,{" "}
                    {address?.[0]?.geographicalAddress?.street
                      ? `${capitalizeFirstLetterOfEachWord(
                          address?.[0]?.geographicalAddress?.street,
                        )} `
                      : ""}
                    {address?.[0]?.geographicalAddress?.streetNumber &&
                      capitalizeFirstLetterOfEachWord(
                        address?.[0]?.geographicalAddress?.streetNumber,
                      )}
                    {address?.[0]?.geographicalAddress?.apartmentNumber
                      ? `/${address?.[0]?.geographicalAddress?.apartmentNumber}`
                      : ""}
                  </BlockText>
                </div>
                <Button
                  onPress={addressModal.open}
                  size="inline"
                  variant="simple"
                  withUnderline={true}
                >
                  <Text weight="bold">zmień adres</Text>
                </Button>
              </div>
            </>
          )}
          <Separator size="xxs" tone="invisible" />
          {(phase === "phaseEmpty" ||
            phase === "phaseResignation" ||
            !phase) && (
            <div>
              <Button
                display="inlineFlex"
                onPress={
                  phase === "phaseResignation" || !phase
                    ? handleBlockFtth
                    : handleRedirectFtth
                }
                size="inline"
                variant="simple"
              >
                <div className="flex gap-3 items-center">
                  <Text tone="accent">sprzedaj</Text>
                  <Icon
                    height={18}
                    name="arrow-right"
                    tone="accent"
                    width={18}
                  />
                </div>
              </Button>
            </div>
          )}
          {phase === "phaseInProgress" && ftth?.orderId && (
            <>
              <Separator
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "xs",
                }}
                tone="invisible"
              />
              <div className="flex flex-row">
                <Text className="min-w-[108px]" weight="bold">
                  nr zamówienia:
                </Text>
                <Text className="pl-1" tone="accent" weight="bold">
                  {ftth.orderId}
                </Text>
                <Button
                  display="inline"
                  onPress={() => {
                    handleCopyToClipboard(ftth.orderId as string);
                  }}
                  size="inline"
                  variant="simple"
                >
                  <Icon className="ml-2 mb-2" name="copy" />
                </Button>
              </div>
            </>
          )}
          {phase === "phaseCompleted" && serviceId && (
            <>
              <Separator
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "xs",
                }}
                tone="invisible"
              />
              <div className="flex flex-row">
                <Text weight="bold">numer usługi:</Text>
                <Text className="pl-1" tone="accent" weight="bold">
                  {serviceId}
                </Text>
              </div>
            </>
          )}
        </div>
      </Panel>
      {addressModal.isOpened && (
        <Modal onClose={addressModal.close}>
          <AddAddressModal
            onClose={addressModal.close}
            onConfirm={onUpdateAddress}
          />
        </Modal>
      )}
    </div>
  );
}
